// Sistema de Monitoreo de Errores usando Google Analytics 4
// Especializado para aplicaciones web con métricas detalladas

// Función para verificar si GA4 está disponible
const isGA4Available = () => {
  return typeof window !== 'undefined' && window.gtag;
};

// Función para trackear errores en GA4
export const trackError = (error, context = {}) => {
  const errorInfo = {
    error_message: error?.message || error?.toString() || 'Unknown error',
    error_name: error?.name || 'Error',
    error_stack: error?.stack || '',
    error_type: 'javascript_error',
    timestamp: new Date().toISOString(),
    url: window.location.href,
    user_agent: navigator.userAgent,
    ...context
  };

  // Trackear en GA4 si está disponible
  if (isGA4Available()) {
    try {
      // Evento principal de error
      window.gtag('event', 'error', {
        event_category: 'Error Tracking',
        event_label: errorInfo.error_message,
        value: 1,
        custom_map: {
          'error_message': 'error_message',
          'error_name': 'error_name',
          'error_stack': 'error_stack',
          'error_type': 'error_type',
          'url': 'url',
          'user_agent': 'user_agent'
        },
        // Datos personalizados
        error_message: errorInfo.error_message,
        error_name: errorInfo.error_name,
        error_stack: errorInfo.error_stack,
        error_type: errorInfo.error_type,
        url: errorInfo.url,
        user_agent: errorInfo.user_agent,
        // Contexto adicional
        ...context
      });

      // También trackear como evento de excepción
      window.gtag('event', 'exception', {
        description: errorInfo.error_message,
        fatal: false,
        custom_map: {
          'error_message': 'error_message',
          'error_name': 'error_name',
          'url': 'url'
        },
        error_message: errorInfo.error_message,
        error_name: errorInfo.error_name,
        url: errorInfo.url
      });

    } catch (gaError) {
      // Evitar logs en consola para no entrar en ciclos con interceptores
    }
  }

  return errorInfo;
};

// Función para trackear warnings
export const trackWarning = (message, context = {}) => {
  const warningInfo = {
    warning_message: message,
    warning_type: 'warning',
    timestamp: new Date().toISOString(),
    url: window.location.href,
    user_agent: navigator.userAgent,
    ...context
  };

  if (isGA4Available()) {
    try {
      window.gtag('event', 'warning', {
        event_category: 'Warning Tracking',
        event_label: message,
        value: 1,
        custom_map: {
          'warning_message': 'warning_message',
          'warning_type': 'warning_type',
          'url': 'url'
        },
        warning_message: message,
        warning_type: 'warning',
        url: window.location.href,
        ...context
      });
    } catch (gaError) {
      // Evitar logs en consola para no entrar en ciclos con interceptores
    }
  }

  return warningInfo;
};

// Función para trackear errores de rendimiento
export const trackPerformanceIssue = (issue, context = {}) => {
  const performanceInfo = {
    issue_type: 'performance',
    issue_description: issue,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    performance_metrics: {
      load_time: performance.now(),
      memory_usage: performance.memory ? performance.memory.usedJSHeapSize : 'N/A',
      navigation_type: performance.navigation?.type || 'N/A'
    },
    ...context
  };

  console.warn('🐌 Performance issue tracked in GA4:', performanceInfo);

  if (isGA4Available()) {
    try {
      window.gtag('event', 'performance_issue', {
        event_category: 'Performance',
        event_label: issue,
        value: 1,
        custom_map: {
          'issue_type': 'issue_type',
          'issue_description': 'issue_description',
          'load_time': 'load_time',
          'memory_usage': 'memory_usage'
        },
        issue_type: 'performance',
        issue_description: issue,
        load_time: performance.now(),
        memory_usage: performance.memory ? performance.memory.usedJSHeapSize : 'N/A',
        ...context
      });
    } catch (gaError) {
      console.error('Error tracking performance issue in GA4:', gaError);
    }
  }

  return performanceInfo;
};

// Función para trackear errores de componentes React
export const trackComponentError = (componentName, error, context = {}) => {
  return trackError(error, {
    error_type: 'react_component_error',
    component_name: componentName,
    ...context
  });
};

// Función para trackear errores de API
export const trackAPIError = (endpoint, error, context = {}) => {
  return trackError(error, {
    error_type: 'api_error',
    api_endpoint: endpoint,
    ...context
  });
};

// Función para trackear errores de navegación
export const trackNavigationError = (from, to, error, context = {}) => {
  return trackError(error, {
    error_type: 'navigation_error',
    navigation_from: from,
    navigation_to: to,
    ...context
  });
};

// Función para verificar el estado de GA4
export const checkGA4Status = () => {
  if (isGA4Available()) {
    console.log('✅ GA4 está disponible para tracking de errores');
    return true;
  } else {
    console.warn('❌ GA4 no está disponible');
    return false;
  }
};

// Función para obtener métricas de errores desde GA4
export const getErrorMetrics = () => {
  if (!isGA4Available()) {
    console.warn('GA4 no está disponible para obtener métricas');
    return null;
  }

  // Aquí podrías implementar llamadas a la API de GA4
  // para obtener métricas de errores en tiempo real
  console.log('📊 Métricas de errores disponibles en GA4 dashboard');
  return 'Métricas disponibles en GA4 dashboard';
};

// Función para testing del sistema
export const testErrorTracking = () => {
  console.log('🧪 Testing GA4 error tracking system...');
  
  // Test 1: Error simple
  try {
    throw new Error('Test error for GA4 tracking');
  } catch (error) {
    trackError(error, { test: 'simple_error' });
  }

  // Test 2: Warning
  trackWarning('Test warning for GA4 tracking', { test: 'warning_test' });

  // Test 3: Performance issue
  trackPerformanceIssue('Test performance issue', { test: 'performance_test' });

  // Test 4: Component error
  trackComponentError('TestComponent', new Error('Test component error'), { test: 'component_test' });

  console.log('✅ GA4 error tracking tests completed');
};

// Exportar funciones para uso global en desarrollo
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.trackError = trackError;
  window.trackWarning = trackWarning;
  window.trackPerformanceIssue = trackPerformanceIssue;
  window.trackComponentError = trackComponentError;
  window.trackAPIError = trackAPIError;
  window.trackNavigationError = trackNavigationError;
  window.checkGA4Status = checkGA4Status;
  window.testErrorTracking = testErrorTracking;
}
